/**
 * Authored challenge constraints for the local “Beat My Edit” slice.
 * Enforce only constraints present on the challenge payload.
 */
import {
  effectiveSound,
  isAudibleSound,
  resolveEdit,
  type ChallengeConstraintId,
  type EditGraph,
  type SourcePack,
} from "@desk/core";
import { OMINOUS_AUDIO_ASSET_ID } from "../../content/ids.js";

export const CONSTRAINT_LABELS: Record<ChallengeConstraintId, string> = {
  "keep-dialogue-context": "Keep dialogue / context beat",
  "no-ominous-sound": "No ominous sound",
  "deadline-bonus": "Optional deadline bonus",
};

export interface ConstraintViolation {
  constraintId: ChallengeConstraintId;
  message: string;
}

/** Beats that must stay retained under keep-dialogue-context. */
export function requiredKeepDialogueContextBeats(pack: SourcePack): string[] {
  const withDialogue = pack.beats
    .filter((b) => Array.isArray(b.dialogue) && b.dialogue.length > 0)
    .map((b) => b.id);
  if (withDialogue.length > 0) return withDialogue;
  return pack.beats.filter((b) => b.contextCritical).map((b) => b.id);
}

function tagsAreOminous(tags: readonly string[]): boolean {
  return tags.includes("ominous") || tags.includes("alarm");
}

function isOminousAudioAsset(pack: SourcePack, assetId: string): boolean {
  if (assetId === OMINOUS_AUDIO_ASSET_ID) return true;
  const asset = pack.assets.find((a) => a.id === assetId);
  if (asset && tagsAreOminous(asset.tags)) return true;
  const option = (pack.sfxOptions ?? []).find((o) => o.assetId === assetId);
  return Boolean(option && tagsAreOminous(option.tags));
}

export function evaluateChallengeConstraints(
  pack: SourcePack,
  graph: EditGraph,
  constraintIds: readonly ChallengeConstraintId[],
): ConstraintViolation[] {
  const violations: ConstraintViolation[] = [];
  const resolved = resolveEdit(graph, pack);

  if (constraintIds.includes("keep-dialogue-context")) {
    const required = new Set(requiredKeepDialogueContextBeats(pack));
    for (const beatId of required) {
      const cut = resolved.cuts.get(beatId);
      if (cut && cut.mode === "omit") {
        violations.push({
          constraintId: "keep-dialogue-context",
          message: `Must keep beat ${beatId}`,
        });
      }
    }
  }

  if (constraintIds.includes("no-ominous-sound")) {
    const sound = effectiveSound(resolved);
    if (sound && isAudibleSound(sound) && isOminousAudioAsset(pack, sound.assetId)) {
      violations.push({
        constraintId: "no-ominous-sound",
        message: "Ominous / alarm sound is not allowed",
      });
    } else {
      for (const sfx of resolved.sfx.values()) {
        if (sfx.assetId === null || sfx.volumeMilli <= 0) continue;
        if (isOminousAudioAsset(pack, sfx.assetId)) {
          violations.push({
            constraintId: "no-ominous-sound",
            message: "Ominous / alarm SFX is not allowed",
          });
          break;
        }
      }
    }
  }

  // deadline-bonus is a soft flag only — never a hard fail.
  return violations;
}

export function constraintsSatisfied(
  pack: SourcePack,
  graph: EditGraph,
  constraintIds: readonly ChallengeConstraintId[],
): boolean {
  return evaluateChallengeConstraints(pack, graph, constraintIds).length === 0;
}

/** Soft deadline window (ms) for optional challenge cash bonus — never a hard fail. */
export const CHALLENGE_DEADLINE_BONUS_MS = 90_000;
export const CHALLENGE_DEADLINE_CASH_BONUS = 15;

export function deadlineBonusEarned(
  constraintIds: readonly ChallengeConstraintId[],
  startedAtMs: number | null,
  finishedAtMs: number,
): boolean {
  if (!constraintIds.includes("deadline-bonus")) return false;
  if (startedAtMs == null) return false;
  return finishedAtMs - startedAtMs <= CHALLENGE_DEADLINE_BONUS_MS;
}
